import React, { useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import styled from '@2pulse/responsive';
import { MainLayout } from '@app/components';
import { ProductHeader } from '@app/features';
import { correspondALaRecherche, desactivesParRicochet, estEnRupture, ingredientsEnRupture, LigneDeRupture } from '@app/features/Rupture';
import { extractData } from '@app/helpers/utils';
import { normalize } from '@app/helpers';
import { useThrottle } from '@app/hooks/useThrottle';
import { useToast } from '@app/hooks/useToast';
import { ingredientApi, packApi, productApi } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import type { IIngredient } from '@app/common';

// RUPTURES DE STOCK, page du dashboard (Maxence, 1er nov. 2026 : « il faut
// que les restaurants, s'ils se connectent sur le dashboard, puissent
// désactiver un ingrédient pour le mettre en rupture, comme sur l'OS »). La
// page agit sur le restaurant de la barre latérale — l'en-tête `x-shop` de
// chaque requête — et reprend la page de la caisse : une recherche dans les
// ingrédients de l'enseigne, chaque ligne avec sa bascule qui s'enregistre
// tout de suite ; en dessous, les ingrédients en rupture dans ce restaurant,
// puis les produits, variations et menus désactivés par ricochet, en lecture
// seule. Toute règle est dans `features/Rupture/ruptures.ts` ; l'écran
// orchestre et écrit.

const POPULATE_MENUS = [
  'packToProductVariations',
  'packToProductVariations.productVariation',
  'packToProductVariations.productVariation.productVariationToIngredients',
  'packToSteps',
  'packToSteps.step',
  'packToSteps.step.stepToProductVariations',
  'packToSteps.step.stepToProductVariations.productVariation',
  'packToSteps.step.stepToProductVariations.productVariation.productVariationToIngredients',
];

export const RuptureScreen = () => {
  const toast = useToast();
  const selectedShop = useAppSelector((state) => state.app.selectedShop);
  const shopId = selectedShop?._id;

  const { data: ingredientsData, isFetching } = ingredientApi.useReadManyIngredientQuery(
    { sort: ['name:ASC'], populate: ['ingredientShops'] },
    { skip: !shopId },
  );
  const ingredients = useMemo(() => extractData(ingredientsData), [ingredientsData]);

  const { data: productsData } = productApi.useReadManyProductsQuery(
    { sort: ['name:ASC'], populate: ['productVariations', 'productVariations.productVariationToIngredients'] },
    { skip: !shopId },
  );
  const { data: packsData } = packApi.useReadManyPacksQuery(
    { sort: ['name:ASC'], populate: POPULATE_MENUS as never },
    { skip: !shopId },
  );
  const products = useMemo(() => extractData(productsData), [productsData]);
  const packs = useMemo(() => extractData(packsData), [packsData]);

  const [mettreEnRupture] = ingredientApi.useCreateIngredientOutOfStockMutation();
  const [remettreEnStock] = ingredientApi.useDeleteIngredientOutOfStockMutation();
  const [enCours, setEnCours] = useState<string | null>(null);

  const [saisie, setSaisie] = useState('');
  const recherche = useThrottle(saisie, 250);

  const enRupture = useMemo(() => (shopId ? ingredientsEnRupture(ingredients, shopId) : []), [ingredients, shopId]);
  const trouves = useMemo(
    () => (recherche.trim() === '' ? [] : ingredients.filter((ingredient) => correspondALaRecherche(ingredient, recherche))),
    [ingredients, recherche],
  );
  const ricochet = useMemo(
    () => desactivesParRicochet({ products, packs }, enRupture.map((ingredient) => ingredient._id)),
    [products, packs, enRupture],
  );

  const basculer = async (ingredient: IIngredient) => {
    if (!shopId || enCours !== null) return;
    setEnCours(ingredient._id);
    try {
      if (estEnRupture(ingredient, shopId)) await remettreEnStock({ ingredientId: ingredient._id }).unwrap();
      else await mettreEnRupture({ ingredientId: ingredient._id }).unwrap();
      // La bascule faite, la recherche se vide (Maxence, 1er nov. 2026) : la
      // liste « En rupture » suffit à constater le résultat.
      setSaisie('');
    } catch {
      toast.error(`Impossible de modifier la rupture de ${ingredient.name}.`);
    } finally {
      setEnCours(null);
    }
  };

  const ligneDIngredient = (ingredient: IIngredient, sousLigne?: string) => (
    <LigneDeRupture
      key={ingredient._id}
      nom={ingredient.name}
      sousLigne={sousLigne}
      enRupture={shopId !== undefined && estEnRupture(ingredient, shopId)}
      enCours={enCours === ingredient._id}
      onBasculer={() => basculer(ingredient)}
      testID={`rupture-${ingredient._id}`} />
  );

  if (!selectedShop) {
    return (
      <MainLayout header={<ProductHeader />}>
        <Vide>
          <VideTexte>Veuillez sélectionner un restaurant pour gérer ses ruptures de stock</VideTexte>
        </Vide>
      </MainLayout>
    );
  }

  const nombreDeRicochets = ricochet.produits.length + ricochet.variations.length + ricochet.menus.length;
  const sousTitre = enRupture.length === 0
    ? `Aucune rupture dans ${selectedShop.publicName}`
    : enRupture.length === 1 ? `1 ingrédient en rupture dans ${selectedShop.publicName}` : `${enRupture.length} ingrédients en rupture dans ${selectedShop.publicName}`;

  return (
    <MainLayout header={<ProductHeader />}>
      <ScreenContainer testID="page-des-ruptures">
        <HeaderSection>
          <HeaderTitle>Ruptures de stock</HeaderTitle>
          <HeaderSubtitle>{sousTitre}</HeaderSubtitle>
        </HeaderSection>

        <ContentContainer>
          <SearchContainer>
            <Recherche
              placeholder="Rechercher un ingrédient…"
              placeholderTextColor="rgba(255,255,255,0.5)"
              value={saisie}
              onChangeText={setSaisie}
              autoCorrect={false}
              testID="rupture-recherche" />
            {saisie.length > 0 && (
              <ClearButton onPress={() => setSaisie('')} accessibilityLabel="Effacer la recherche">
                <ClearIcon>×</ClearIcon>
              </ClearButton>
            )}
          </SearchContainer>

          <ScrollView showsVerticalScrollIndicator={false}>
            {recherche.trim() !== '' && (
              <>
                <SectionHeader>
                  <SectionTitle>RÉSULTATS</SectionTitle>
                  <SectionCount>{trouves.length}</SectionCount>
                </SectionHeader>
                {trouves.length === 0 && !isFetching && <SectionVide>Aucun ingrédient ne correspond</SectionVide>}
                {trouves.map((ingredient) => ligneDIngredient(ingredient, shopId !== undefined && estEnRupture(ingredient, shopId) ? 'Déjà en rupture' : undefined))}
              </>
            )}

            <SectionHeader>
              <SectionTitle>EN RUPTURE</SectionTitle>
              <SectionCount>{enRupture.length}</SectionCount>
            </SectionHeader>
            {enRupture.length === 0
              ? <SectionVide>Tous les ingrédients sont en stock dans ce restaurant</SectionVide>
              : enRupture.map((ingredient) => ligneDIngredient(ingredient))}

            <SectionHeader>
              <SectionTitle>DÉSACTIVÉS PAR RICOCHET</SectionTitle>
              <SectionCount>{nombreDeRicochets}</SectionCount>
            </SectionHeader>
            {nombreDeRicochets === 0
              ? <SectionVide>Aucun produit ni menu n'est touché</SectionVide>
              : (
                <>
                  {ricochet.produits.map((produit) => (
                    <LigneDeRupture key={`p-${produit._id}`} nom={produit.name} sousLigne="Produit" enRupture testID={`ricochet-produit-${produit._id}`} />
                  ))}
                  {ricochet.variations.map(({ produit, variation }) => (
                    <LigneDeRupture key={`v-${variation._id}`} nom={variation.name || produit.name} sousLigne={`Variation de ${produit.name}`} enRupture testID={`ricochet-variation-${variation._id}`} />
                  ))}
                  {ricochet.menus.map((menu) => (
                    <LigneDeRupture key={`m-${menu._id}`} nom={menu.name} sousLigne="Menu" enRupture testID={`ricochet-menu-${menu._id}`} />
                  ))}
                </>
              )}
          </ScrollView>
        </ContentContainer>
      </ScreenContainer>
    </MainLayout>
  );
};

// Mêmes conteneurs que la page des prix par restaurant (`CatalogShopScreen`) :
// l'en-tête sur le fond clair de la page, décalé de la barre latérale, puis un
// bloc navy arrondi qui porte la recherche et les sections.
const ScreenContainer = styled.View(({ theme }) => ({
  flex: 1,
  paddingTop: normalize(20),
  paddingRight: normalize(20),
  paddingBottom: normalize(20),
  paddingLeft: normalize(20) + theme.sizes.sidebarWidth,
}));

const HeaderSection = styled.View({
  marginBottom: normalize(24),
});

const HeaderTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(28),
  fontWeight: '700',
  color: theme.colors.oxfordblue,
  marginBottom: normalize(4),
}));

const HeaderSubtitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
}));

const ContentContainer = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.oxfordblue,
  borderRadius: normalize(12),
  padding: normalize(20),
  overflow: 'hidden',
}));

const SearchContainer = styled.View({
  position: 'relative',
  width: normalize(360),
  marginBottom: normalize(8),
});

const Recherche = styled.TextInput(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: normalize(8),
  paddingVertical: normalize(10),
  paddingHorizontal: normalize(16),
  paddingRight: normalize(40),
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
}));

const ClearButton = styled.TouchableOpacity({
  position: 'absolute',
  right: normalize(12),
  top: '50%',
  transform: [{ translateY: -normalize(10) }],
  width: normalize(20),
  height: normalize(20),
  alignItems: 'center',
  justifyContent: 'center',
});

const ClearIcon = styled.Text(({ theme }) => ({
  color: theme.colors.babypowder,
  fontSize: normalize(18),
  fontWeight: 'bold',
}));

const SectionHeader = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: normalize(16),
  marginBottom: normalize(12),
  paddingHorizontal: normalize(8),
});

const SectionTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  fontWeight: '700',
  color: theme.colors.gold,
  letterSpacing: 2,
}));

const SectionCount = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  color: 'rgba(255,255,255,0.5)',
  marginLeft: normalize(8),
});

const SectionVide = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: 'rgba(255,255,255,0.4)',
  textAlign: 'center',
  paddingVertical: normalize(20),
});

const Vide = styled.View(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  padding: normalize(40),
  paddingLeft: normalize(40) + theme.sizes.sidebarWidth,
}));

const VideTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  color: theme.colors.satanGray,
  textAlign: 'center',
}));
